import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkles,
  TrendingUp,
  Brain,
  Heart,
  Battery,
  AlertTriangle,
  Calendar,
  Clock,
  Target,
  Lightbulb,
  ChevronRight,
  Users
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { insightsService } from "@/services/insightsService";
import { wellnessService } from "@/services/wellnessService";
import { cn } from "@/lib/utils";

interface EnhancedInsightsPanelProps {
  className?: string;
}

const EnhancedInsightsPanel = ({ className }: EnhancedInsightsPanelProps) => {
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [patterns, setPatterns] = useState<any | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [selectedTheme, setSelectedTheme] = useState<any | null>(null);

  // Note: createBaselineAnalysis function kept for reference but simplified
  // Backend API provides pattern data directly
  const createBaselineAnalysis = (checkIns: any[]): any | null => {
    if (!checkIns || checkIns.length < 3) {
      return null;
    }

    const numericValues = (values: unknown[]): number[] =>
      values.filter((value): value is number => typeof value === "number");

    const average = (values: number[]): number =>
      values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

    const normalizeImpact = (value: number): number => {
      const centered = (value - 50) / 50;
      const clamped = Math.max(-1, Math.min(1, centered));
      return Number(clamped.toFixed(2));
    };

    const energyValues = numericValues(checkIns.map((c) => c.energy));
    const focusValues = numericValues(checkIns.map((c) => c.focus));
    const moodValues = numericValues(checkIns.map((c) => (typeof c.mood === "number" ? c.mood * 20 : undefined)));

    const avgEnergy = average(energyValues);
    const avgFocus = average(focusValues);
    const avgMood = average(moodValues);

    const sentiment = avgEnergy >= 70 ? "positive" : avgEnergy >= 45 ? "neutral" : "negative";

    const dailyGroups = new Map<number, { energy: number[]; focus: number[] }>();
    const weekdayGroups = new Map<string, { energy: number[]; focus: number[] }>();

    checkIns.forEach((checkIn) => {
      // ALIGNED WITH web_old: Handle both Supabase format (created_at) and backend format (createdAt/date)
      const timestampField = checkIn.created_at || checkIn.createdAt || checkIn.date;
      if (typeof timestampField !== "string" && !(timestampField instanceof Date)) {
        return;
      }

      const timestamp = new Date(timestampField);
      if (Number.isNaN(timestamp.getTime())) return;

      if (typeof checkIn.energy === "number" && typeof checkIn.focus === "number") {
        const hour = timestamp.getHours();
        const day = timestamp.toLocaleDateString("en-US", { weekday: "long" });

        if (!dailyGroups.has(hour)) {
          dailyGroups.set(hour, { energy: [], focus: [] });
        }
        if (!weekdayGroups.has(day)) {
          weekdayGroups.set(day, { energy: [], focus: [] });
        }

        dailyGroups.get(hour)!.energy.push(checkIn.energy);
        dailyGroups.get(hour)!.focus.push(checkIn.focus);
        weekdayGroups.get(day)!.energy.push(checkIn.energy);
        weekdayGroups.get(day)!.focus.push(checkIn.focus);
      }
    });

    const dailyPeaks = Array.from(dailyGroups.entries())
      .map(([hour, values]) => ({
        hour,
        avgEnergy: average(values.energy),
        avgFocus: average(values.focus),
      }))
      .sort((a, b) => b.avgEnergy - a.avgEnergy)
      .slice(0, 3);

    const weeklyPeaks = Array.from(weekdayGroups.entries())
      .map(([day, values]) => ({
        day,
        avgEnergy: average(values.energy),
        avgFocus: average(values.focus),
      }))
      .sort((a, b) => b.avgEnergy - a.avgEnergy)
      .slice(0, 5);

    const blockerCount = checkIns.filter(
      (c) => typeof c.blockers === "string" && c.blockers.trim().length > 0
    ).length;

    return {
      themeAnalysis: [
        {
          theme: "Early Patterns Emerging",
          frequency: checkIns.length,
          sentiment,
          impact: "medium",
          category: "growth",
          relatedWords: ["patterns", "tracking", "baseline"],
          energyCorrelation: Number(avgEnergy.toFixed(1)),
        },
      ],
      correlations: {
        energyDrivers: avgEnergy
          ? [
              {
                factor: "consistent_check_ins",
                impact: normalizeImpact(avgEnergy),
              },
            ]
          : [],
        focusEnablers: avgFocus
          ? [
              {
                factor: "protected_focus_blocks",
                impact: normalizeImpact(avgFocus),
              },
            ]
          : [],
        moodInfluencers: avgMood
          ? [
              {
                factor: "daily_reflection",
                impact: normalizeImpact(avgMood),
              },
            ]
          : [],
      },
      predictions: {
        nextDayEnergy: Math.round(avgEnergy || 55),
        burnoutRisk: Math.max(10, Math.round((100 - avgEnergy) * 0.4)),
        suggestedInterventions: [
          "Keep completing daily check-ins to strengthen your baseline",
          "Schedule a focused work block during your emerging peak time",
          "Capture one small win each day to reinforce positive momentum",
        ],
      },
      behavioralPatterns: {
        productivityCycles: {
          dailyPeaks,
          weeklyPeaks,
          monthlyTrends: [{ week: 1, trend: "stable" }],
        },
        emotionalPatterns: {
          moodTriggers: [],
          resilience: {
            bounceBackTime: 2,
            copingStrategies: ["Daily reflection", "Short breaks"],
          },
          stressIndicators: [],
        },
        workPatterns: {
          taskCompletionRate: checkIns.length
            ? Number(
                (
                  checkIns.filter((c) => Array.isArray(c.tasks) && c.tasks.length > 0).length /
                  checkIns.length
                ).toFixed(2)
              )
            : 0,
          focusSessionSuccess: Number((avgFocus / 100 || 0.4).toFixed(2)),
          collaborationImpact: 0,
          blockerPatterns: blockerCount
            ? [
                {
                  type: "energy",
                  frequency: blockerCount,
                  resolution: [
                    "Capture blockers during the next stand-up",
                    "Schedule a 15-minute unblock session",
                  ],
                },
              ]
            : [],
        },
      },
    };
  };

  // Helper function to generate encouraging suggestions for new users
  const generateStarterSuggestions = async (checkIns: any[]): Promise<string[]> => {
  const avgEnergy = checkIns.reduce((sum, c) => sum + (c.energy || 0), 0) / checkIns.length;
    const checkInCount = checkIns.length;
    
    if (checkInCount === 1) {
      return [
        "Excellent start! Your first check-in shows commitment to self-awareness and growth.",
        "Continue with daily check-ins to establish patterns and unlock deeper insights.",
        "Set a small daily goal to build momentum and track your progress over time."
      ];
    } else if (checkInCount <= 3) {
      return [
        `Great consistency! You've completed ${checkInCount} check-ins and are building a valuable habit.`,
        avgEnergy >= 70 
          ? "Your energy levels look strong - maintain this momentum with consistent daily practices." 
          : "Focus on activities that boost your energy - sleep, exercise, and meaningful work.",
        "Consider setting weekly goals to give direction to your daily check-ins and progress."
      ];
    } else {
      return [
        "Your patterns are starting to emerge! Keep tracking to see deeper insights develop.",
        "You're building excellent self-awareness through consistent check-ins.",
        "Ready to dive deeper? Try setting specific goals for areas you want to improve."
      ];
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadPatterns();
    }
  }, [user?.id]);

  const loadPatterns = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // ALIGNED WITH web_old: Get enhanced patterns from backend API (line 248)
      const analysisResult = await insightsService.getEnhancedPatterns(30).catch(() => null);
      
      // ALIGNED WITH web_old: If no patterns but user has check-ins, provide baseline analysis (lines 250-276)
      if (!analysisResult || (!analysisResult.themeAnalysis?.length && !analysisResult.correlations)) {
        // ALIGNED WITH web_old: Check if user has any check-ins at all (line 253-258)
        // Backend returns check-ins ordered by date descending
        const checkIns = await wellnessService.getCheckIns({ 
          limit: 5,
        }).catch(() => []);

        if (checkIns && checkIns.length > 0) {
          // ALIGNED WITH web_old: Create baseline analysis from recent check-ins (line 262)
          const baselinePatterns = createBaselineAnalysis(checkIns);
          setPatterns(baselinePatterns);
          
          // ALIGNED WITH web_old: Generate encouraging suggestions for new users (line 267)
          if (baselinePatterns) {
            const suggestions = await generateStarterSuggestions(checkIns);
            setAiSuggestions(suggestions);
          } else {
            // Not enough data yet - encourage more check-ins
            setAiSuggestions(['Complete 3-5 check-ins to see your first pattern insights']);
          }
        } else {
          setPatterns(null);
          setAiSuggestions(['Start with your first check-in to begin tracking patterns']);
        }
      } else {
        // ALIGNED WITH web_old: Use enhanced pattern data (line 278)
        setPatterns(analysisResult);
        
        // ALIGNED WITH web_old: Generate contextual suggestions using backend API (line 279)
        // Backend endpoint: GET /insights/enhanced-patterns/suggestions
        // MIGRATED FROM web_old/src/components/dashboard/EnhancedInsightsPanel.tsx::generateContextualSuggestions()
        const suggestions = await insightsService.getContextualSuggestions(30).catch(() => {
          // Fallback to pattern suggestions if AI generation fails
          return analysisResult.predictions?.suggestedInterventions || 
                 ['Continue tracking to unlock more insights'];
        });
        setAiSuggestions(suggestions.slice(0, 3));
      }
    } catch (error) {
      console.error('Error loading enhanced patterns:', error);
      setPatterns(null);
      setAiSuggestions(['Unable to load patterns. Please try again later.']);
    } finally {
      setIsLoading(false);
    }
  };

  // MIGRATED TO BACKEND: generateContextualSuggestions now uses backend API
  // Backend endpoint: GET /insights/enhanced-patterns/suggestions
  // MIGRATED FROM web_old/src/components/dashboard/EnhancedInsightsPanel.tsx::generateContextualSuggestions()
  // This function is no longer used - kept for reference only
  // The loadPatterns function now calls insightsService.getContextualSuggestions() directly

  const getCategoryColor = (category: string) => {
    const colors = {
      emotion: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30',
      work: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
      personal: 'text-green-600 bg-green-100 dark:bg-green-900/30',
      health: 'text-red-600 bg-red-100 dark:bg-red-900/30',
      relationship: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
      growth: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30'
    };
    return colors[category] || 'text-gray-600 bg-gray-100 dark:bg-gray-900/30';
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'negative': return '😔';
      default: return '😐';
    }
  };

  if (isLoading) {
    return (
      <Card className={cn("", className)}>
        <CardContent className="p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="text-muted-foreground">Analyzing your patterns...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!patterns) {
    return (
      <Card className={cn("", className)}>
        <CardContent className="p-8">
          <div className="text-center">
            <Brain className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Deep Pattern Analysis</h3>
            <p className="text-muted-foreground">Complete your first check-in to unlock personalized insights about your productivity patterns, energy cycles, and behavioral trends.</p>
            <p className="text-sm text-muted-foreground mt-2">Our AI will analyze your data to provide actionable recommendations!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Burnout Risk Alert */}
      {patterns.burnoutRisk && (patterns.burnoutRisk.score > 60 || patterns.burnoutRisk.level === 'high') && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-900/20">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-lg">Wellness Alert</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Burnout Risk Level</span>
                <span className="text-2xl font-bold text-orange-600">
                  {Math.round(patterns.burnoutRisk.score || 0)}%
                </span>
              </div>
              <Progress 
                value={patterns.burnoutRisk.score || 0} 
                className="h-2"
              />
              {patterns.burnoutRisk.factors && patterns.burnoutRisk.factors.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-2">Factors identified:</p>
                  <ul className="space-y-1">
                    {patterns.burnoutRisk.factors.slice(0, 3).map((factor: string, idx: number) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <ChevronRight className="h-3 w-3 mt-0.5 text-orange-600" />
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {patterns.burnoutRisk.recommendations && patterns.burnoutRisk.recommendations.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-2">Recommended actions:</p>
                  <ul className="space-y-1">
                    {patterns.burnoutRisk.recommendations.slice(0, 3).map((recommendation: string, idx: number) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <ChevronRight className="h-3 w-3 mt-0.5 text-orange-600" />
                        <span>{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Insights Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <CardTitle>Deep Pattern Analysis</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={loadPatterns}>
              <Sparkles className="h-4 w-4" />
              Refresh
            </Button>
          </div>
          <CardDescription>
            AI-powered analysis of your patterns, energy drivers, and behavioral trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="themes" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="themes">Themes</TabsTrigger>
              <TabsTrigger value="productivity">Productivity</TabsTrigger>
              <TabsTrigger value="emotional">Emotional</TabsTrigger>
              <TabsTrigger value="correlations">Drivers</TabsTrigger>
            </TabsList>

            {/* Themes Tab - Simplified for backend pattern data */}
            <TabsContent value="themes" className="space-y-4 mt-4">
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Pattern Discovery:</strong> Your patterns reveal insights about your energy and productivity. 
                  Backend analysis provides recommendations based on your check-in data.
                </p>
              </div>
              
              {/* Show energy patterns if available */}
              {patterns.energyPatterns && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Energy Patterns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm">Average Energy</span>
                          <span className="text-lg font-bold">{Math.round(patterns.energyPatterns.averageEnergy || 0)}%</span>
                        </div>
                        <Progress value={patterns.energyPatterns.averageEnergy || 0} className="h-2" />
                      </div>
                      {patterns.energyPatterns.trend && (
                        <div>
                          <span className="text-sm text-muted-foreground">Trend: </span>
                          <span className="text-sm font-medium capitalize">{patterns.energyPatterns.trend}</span>
                        </div>
                      )}
                      {patterns.energyPatterns.peakTimes && patterns.energyPatterns.peakTimes.length > 0 && (
                        <div>
                          <span className="text-sm text-muted-foreground">Peak times: </span>
                          <span className="text-sm">{patterns.energyPatterns.peakTimes.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Fallback message if no theme analysis available */}
              {(!patterns.themeAnalysis || patterns.themeAnalysis.length === 0) && (
                <div className="text-center py-8">
                  <div className="bg-muted/30 p-6 rounded-lg border">
                    <p className="text-muted-foreground mb-2">Pattern analysis available</p>
                    <p className="text-sm text-muted-foreground">
                      Continue checking in to see detailed pattern insights!
                    </p>
                  </div>
                </div>
              )}
              
              {/* Original theme analysis display - kept for compatibility if structure matches */}
              {patterns.themeAnalysis && patterns.themeAnalysis.length > 0 && (
              <div className="grid gap-3">
                {patterns.themeAnalysis.slice(0, 5).map((theme: any, idx: number) => {
                  const readableTheme = theme.theme.includes('_theme') 
                    ? `${theme.category} topics` 
                    : theme.theme.replace(/_/g, ' ');
                  
                  return (
                    <Card 
                      key={idx} 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedTheme(theme)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge className={cn("text-xs", getCategoryColor(theme.category))}>
                                {theme.category}
                              </Badge>
                              <span className="text-lg">{getSentimentIcon(theme.sentiment)}</span>
                            </div>
                            <div>
                              <p className="font-medium capitalize">{readableTheme}</p>
                              <p className="text-sm text-muted-foreground">
                                {theme.sentiment === 'positive' ? 'Energy booster' : 
                                 theme.sentiment === 'negative' ? 'Energy drainer' : 
                                 'Neutral impact'}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">
                                {theme.frequency} {theme.frequency === 1 ? 'mention' : 'mentions'} this month
                              </p>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                  <div 
                                    className={cn(
                                      "h-full rounded-full",
                                      theme.sentiment === 'positive' ? "bg-green-500" : 
                                      theme.sentiment === 'negative' ? "bg-red-500" : 
                                      "bg-yellow-500"
                                    )}
                                    style={{ width: `${Math.round(theme.energyCorrelation || 50)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium">{Math.round(theme.energyCorrelation || 0)}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Impact</p>
                              <p className={cn(
                                "text-lg font-bold",
                                theme.impact === 'high' ? "text-red-600" : 
                                theme.impact === 'medium' ? "text-yellow-600" : 
                                "text-green-600"
                              )}>
                                {theme.impact === 'high' ? 'High' : 
                                 theme.impact === 'medium' ? 'Medium' : 
                                 'Low'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              )}
            </TabsContent>

            {/* Productivity Tab - Simplified for backend pattern data */}
            <TabsContent value="productivity" className="space-y-4 mt-4">
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Peak Performance Insights:</strong> Your body has natural rhythms. Working with them (not against them) 
                  can boost productivity by up to 30%. Schedule important tasks during your peak hours.
                </p>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                {/* Energy Patterns */}
                {patterns.energyPatterns && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Your Energy Patterns
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm">Average Energy</span>
                          <span className="text-lg font-bold">{Math.round(patterns.energyPatterns.averageEnergy || 0)}%</span>
                        </div>
                        <Progress value={patterns.energyPatterns.averageEnergy || 0} className="h-2" />
                      </div>
                      {patterns.energyPatterns.peakTimes && patterns.energyPatterns.peakTimes.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Peak Times:</p>
                          <div className="space-y-1">
                            {patterns.energyPatterns.peakTimes.slice(0, 3).map((time: string, idx: number) => (
                              <div key={idx} className="text-sm flex items-center gap-2">
                                <Battery className="h-3 w-3 text-green-600" />
                                <span>{time}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                )}
              </div>
              
              {/* Original productivity cycles display - kept for compatibility if structure matches */}
              {patterns.behavioralPatterns?.productivityCycles?.dailyPeaks && patterns.behavioralPatterns.productivityCycles.dailyPeaks.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Your Biological Prime Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {patterns.behavioralPatterns.productivityCycles.dailyPeaks.slice(0, 3).map((peak: any, idx: number) => {
                        const timeString = peak.hour === 12 ? '12:00 PM' : peak.hour > 12 ? `${peak.hour - 12}:00 PM` : peak.hour === 0 ? '12:00 AM' : `${peak.hour}:00 AM`;
                        const isTopPeak = idx === 0;
                        return (
                          <div key={idx} className={cn(
                            "p-2 rounded-lg",
                            isTopPeak ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800" : ""
                          )}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium">{timeString}</span>
                                {isTopPeak && <p className="text-xs text-green-600 dark:text-green-400">Your sweet spot!</p>}
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <div className="flex items-center gap-1">
                                    <Battery className="h-3 w-3 text-green-600" />
                                    <span className="text-sm font-medium">{Math.round(peak.avgEnergy)}%</span>
                                  </div>
                                  <p className="text-xs text-muted-foreground">energy</p>
                                </div>
                                <div className="text-right">
                                  <div className="flex items-center gap-1">
                                    <Target className="h-3 w-3 text-blue-600" />
                                    <span className="text-sm font-medium">{Math.round(peak.avgFocus)}%</span>
                                  </div>
                                  <p className="text-xs text-muted-foreground">focus</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Work Patterns */}
              {patterns.behavioralPatterns?.workPatterns && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Your Work Style
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm">Deep Focus Success</span>
                          <span className="text-lg font-bold">
                            {Math.round(patterns.behavioralPatterns.workPatterns.focusSessionSuccess * 100)}%
                          </span>
                        </div>
                        <Progress 
                          value={patterns.behavioralPatterns.workPatterns.focusSessionSuccess * 100} 
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {patterns.behavioralPatterns.workPatterns.focusSessionSuccess > 0.7 
                            ? "You're in the zone! Keep protecting your focus time."
                            : "Try time-blocking to improve deep work sessions."}
                        </p>
                      </div>
                      
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="h-3 w-3 text-blue-600" />
                          <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                            Collaboration Style
                          </span>
                        </div>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          {patterns.behavioralPatterns.workPatterns.collaborationImpact > 0.2 
                            ? "Working with others energizes you (+" + Math.round(patterns.behavioralPatterns.workPatterns.collaborationImpact * 100) + "% energy)"
                            : patterns.behavioralPatterns.workPatterns.collaborationImpact < -0.2
                            ? "You perform best with solo focus time"
                            : "You balance solo and collaborative work well"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Blocker Patterns */}
              {patterns.behavioralPatterns?.workPatterns?.blockerPatterns && patterns.behavioralPatterns.workPatterns.blockerPatterns.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">What Slows You Down (& How to Fix It)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {patterns.behavioralPatterns.workPatterns.blockerPatterns.slice(0, 3).map((blocker, idx) => {
                        const blockerIcons = {
                          'technical': '🔧',
                          'communication': '💬',
                          'planning': '📋',
                          'energy': '🔋',
                          'focus': '🎯',
                          'resources': '📚'
                        };
                        const icon = blockerIcons[blocker.type] || '❗';
                        
                        return (
                          <div key={idx} className="space-y-2 pb-3 border-b last:border-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium capitalize text-sm flex items-center gap-2">
                                <span className="text-lg">{icon}</span>
                                {blocker.type.replace(/_/g, ' ')}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {blocker.frequency} {blocker.frequency === 1 ? 'time' : 'times'}
                              </Badge>
                            </div>
                            <div className="pl-7">
                              <p className="text-xs text-muted-foreground mb-1">Try these solutions:</p>
                              <div className="flex flex-wrap gap-1">
                                {blocker.resolution.map((solution, sIdx) => (
                                  <Badge key={sIdx} variant="secondary" className="text-xs">
                                    {solution}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Emotional Tab - Simplified */}
            <TabsContent value="emotional" className="space-y-4 mt-4">
              <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  <strong>Emotional Intelligence:</strong> Understanding your mood patterns helps you prepare for challenges 
                  and maximize good days. Your emotional resilience directly impacts your performance and wellbeing.
                </p>
              </div>
              {/* Mood Triggers */}
              {patterns?.behavioralPatterns?.emotionalPatterns?.moodTriggers && patterns.behavioralPatterns.emotionalPatterns.moodTriggers.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    What Affects Your Mood
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {patterns.behavioralPatterns.emotionalPatterns.moodTriggers.slice(0, 4).map((trigger, idx) => {
                      const isPositive = trigger.moodChange > 0;
                      const triggerEmojis = {
                        'achievement': '🎯',
                        'social_interaction': '👥',
                        'obstacles': '🚧',
                        'stress': '😰',
                        'energy_boost': '⚡',
                        'energy_drop': '🔋',
                        'task_completion': '✅'
                      };
                      const emoji = triggerEmojis[trigger.trigger] || (isPositive ? '😊' : '😔');
                      
                      return (
                        <div key={idx} className={cn(
                          "p-2 rounded-lg flex items-center justify-between",
                          isPositive ? "bg-green-50 dark:bg-green-900/20" : "bg-red-50 dark:bg-red-900/20"
                        )}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{emoji}</span>
                            <span className="text-sm capitalize">{trigger.trigger.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <span className={cn(
                                "text-sm font-bold block",
                                isPositive ? "text-green-600" : "text-red-600"
                              )}>
                                {trigger.moodChange > 0 ? '+' : ''}{trigger.moodChange.toFixed(1)}
                              </span>
                              <span className="text-xs text-muted-foreground">mood</span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {trigger.frequency}x
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Resilience & Coping */}
              {patterns?.behavioralPatterns?.emotionalPatterns?.resilience && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Your Emotional Resilience
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Recovery Speed</p>
                          <p className="text-xs text-muted-foreground">After difficult days</p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-purple-600">
                            {patterns.behavioralPatterns.emotionalPatterns.resilience.bounceBackTime?.toFixed(1) ?? 'N/A'}
                          </span>
                          <span className="text-sm text-purple-600"> days</span>
                          <p className="text-xs text-muted-foreground">
                            {(patterns.behavioralPatterns.emotionalPatterns.resilience.bounceBackTime ?? 0) < 2 
                              ? "Fast recovery!" 
                              : (patterns.behavioralPatterns.emotionalPatterns.resilience.bounceBackTime ?? 0) < 4
                              ? "Good resilience"
                              : "Needs support"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Your Coping Strategies:</p>
                      <div className="flex flex-wrap gap-2">
                        {(patterns.behavioralPatterns.emotionalPatterns.resilience.copingStrategies ?? []).map((strategy, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {strategy.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {(patterns.behavioralPatterns.emotionalPatterns.stressIndicators?.length ?? 0) > 0 && (
                      <div className="pt-2">
                        <p className="text-sm font-medium mb-2">Stress Indicators:</p>
                        <div className="flex flex-wrap gap-2">
                          {(patterns.behavioralPatterns.emotionalPatterns.stressIndicators ?? []).map((indicator, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs text-orange-600">
                              {indicator.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* Drivers Tab - Simplified */}
            <TabsContent value="correlations" className="space-y-4 mt-4">
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-800 dark:text-green-200">
                  <strong>Your Personal Formula:</strong> These are the specific activities and conditions that predictably 
                  boost or drain your energy. Use this data to design your ideal day.
                </p>
              </div>
              {/* Energy Drivers */}
              {patterns?.correlations?.energyDrivers && patterns.correlations.energyDrivers.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Battery className="h-4 w-4" />
                    What Drives Your Energy
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {patterns.correlations.energyDrivers.slice(0, 5).map((driver, idx) => {
                      const isPositive = driver.impact > 0;
                      const impactPercent = Math.round(Math.abs(driver.impact) * 100);
                      const driverIcons = {
                        'morning_routine': '🌅',
                        'exercise': '🏃',
                        'social_interaction': '👥',
                        'deep_work': '💻',
                        'breaks': '☕',
                        'meetings': '📅',
                        'learning': '📚'
                      };
                      const icon = driverIcons[driver.factor] || (isPositive ? '⚡' : '🔋');
                      
                      return (
                        <div key={idx} className={cn(
                          "p-3 rounded-lg",
                          isPositive ? "bg-green-50 dark:bg-green-900/20" : "bg-red-50 dark:bg-red-900/20"
                        )}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{icon}</span>
                              <span className="text-sm font-medium capitalize">
                                {driver.factor.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <span className={cn(
                              "text-lg font-bold",
                              isPositive ? "text-green-600" : "text-red-600"
                            )}>
                              {isPositive ? '+' : '-'}{impactPercent}%
                            </span>
                          </div>
                          <div className="relative">
                            <Progress 
                              value={impactPercent} 
                              className="h-2"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {isPositive 
                                ? `Boosts your energy by ${impactPercent}%`
                                : `Drains your energy by ${impactPercent}%`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Focus Enablers */}
              {patterns?.correlations?.focusEnablers && patterns.correlations.focusEnablers.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Focus Enablers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {patterns.correlations.focusEnablers.slice(0, 4).map((enabler, idx) => {
                      const isPositive = enabler.impact > 0;
                      const impactPercent = Math.round(Math.abs(enabler.impact) * 100);
                      
                      return (
                        <div key={idx} className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                          <div className="flex items-center justify-between">
                            <span className="text-sm capitalize flex items-center gap-2">
                              <Target className="h-3 w-3 text-blue-600" />
                              {enabler.factor.replace(/_/g, ' ')}
                            </span>
                            <div className="text-right">
                              <span className={cn(
                                "font-bold text-sm",
                                isPositive ? "text-blue-600" : "text-gray-600"
                              )}>
                                {isPositive ? '+' : ''}{impactPercent}%
                              </span>
                              <p className="text-xs text-muted-foreground">focus</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* AI-Powered Suggestions */}
      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg">Your Personalized Action Plan</CardTitle>
          </div>
          <CardDescription>
            Smart suggestions based on your patterns and data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {aiSuggestions.length > 0 ? (
              aiSuggestions.map((suggestion, idx) => (
                <div 
                  key={idx} 
                  className="bg-white/70 dark:bg-card/70 p-4 rounded-lg border border-purple-100 dark:border-border flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-xs font-medium text-purple-600 dark:text-purple-400">
                    {idx + 1}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-foreground leading-relaxed flex-1">
                    {suggestion}
                  </p>
                </div>
              ))
            ) : (
              <div className="bg-white/70 dark:bg-card/70 p-4 rounded-lg border border-purple-100 dark:border-border text-center">
                <div className="flex items-center justify-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-sm font-medium">Analyzing your patterns...</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Keep checking in daily to get personalized recommendations!
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selected Theme Details Modal */}
      {selectedTheme && (
        <Card className="fixed bottom-4 right-4 w-96 shadow-lg z-50 border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className={cn("text-xs", getCategoryColor(selectedTheme.category))}>
                  {selectedTheme.category}
                </Badge>
                <span className="text-lg">{getSentimentIcon(selectedTheme.sentiment)}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedTheme(null)}
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>
            <CardTitle className="text-base capitalize">
              {selectedTheme.theme.includes('_theme') 
                ? `${selectedTheme.category} topics` 
                : selectedTheme.theme.replace(/_/g, ' ')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Frequency</p>
                  <p className="font-semibold">{selectedTheme.frequency} mentions</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Energy Impact</p>
                  <p className="font-semibold">{Math.round(selectedTheme.energyCorrelation || 0)}%</p>
                </div>
              </div>
              
              {selectedTheme.relatedWords.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Common words:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedTheme.relatedWords.slice(0, 8).map((word, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {word}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedTheme.timePatterns && Object.keys(selectedTheme.timePatterns).length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Most common times:</p>
                  <div className="space-y-1">
                    {Object.entries(selectedTheme.timePatterns)
                      .sort((a, b) => (typeof b[1] === 'number' ? b[1] : 0) - (typeof a[1] === 'number' ? a[1] : 0))
                      .slice(0, 3)
                      .map(([time, count]) => (
                        <div key={time} className="flex items-center justify-between text-sm">
                          <span className="capitalize">{time.replace(/_/g, ' ')}</span>
                          <span className="text-muted-foreground">{typeof count === 'number' ? count : 0}x</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  This theme appears when your energy is around {Math.round(selectedTheme.energyCorrelation || 0)}% on average.
                  {selectedTheme.sentiment === 'positive' && ' It seems to boost your mood!'}
                  {selectedTheme.sentiment === 'negative' && ' Consider strategies to address this when it comes up.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnhancedInsightsPanel;